package kpt.core.base.database.infra.dao

import androidx.room3.EntityInsertAdapter
import androidx.room3.RoomDatabase
import androidx.room3.coroutines.createFlow
import androidx.room3.util.getColumnIndexOrThrow
import androidx.room3.util.performSuspending
import androidx.sqlite.SQLiteStatement
import kotlin.Int
import kotlin.Long
import kotlin.String
import kotlin.Suppress
import kotlin.collections.List
import kotlin.collections.MutableList
import kotlin.collections.mutableListOf
import kotlin.reflect.KClass
import kotlinx.coroutines.flow.Flow
import kpt.core.base.database.infra.entity.DraftEntity

@Suppress(names = ["UNCHECKED_CAST", "DEPRECATION", "REDUNDANT_PROJECTION", "REMOVAL", "OPT_IN_USAGE_ERROR", "OPT_IN_USAGE", "MemberExtensionConflict", "CAN_BE_VAL"])
internal class DraftDao_Impl(
  __db: RoomDatabase,
) : DraftDao {
  private val __db: RoomDatabase

  private val __insertAdapterOfDraftEntity: EntityInsertAdapter<DraftEntity>
  init {
    this.__db = __db
    this.__insertAdapterOfDraftEntity = object : EntityInsertAdapter<DraftEntity>() {
      protected override fun createQuery(): String = "INSERT OR REPLACE INTO `framework_submit_drafts` (`id`,`formKey`,`uniqueKey`,`payloadJson`,`status`,`createdAtMs`,`updatedAtMs`,`errorMessage`,`attemptCount`) VALUES (nullif(?, 0),?,?,?,?,?,?,?,?)"

      protected override fun bind(statement: SQLiteStatement, entity: DraftEntity) {
        statement.bindLong(1, entity.id)
        statement.bindText(2, entity.formKey)
        val _tmpUniqueKey: String? = entity.uniqueKey
        if (_tmpUniqueKey == null) {
          statement.bindNull(3)
        } else {
          statement.bindText(3, _tmpUniqueKey)
        }
        statement.bindText(4, entity.payloadJson)
        statement.bindText(5, entity.status)
        statement.bindLong(6, entity.createdAtMs)
        statement.bindLong(7, entity.updatedAtMs)
        val _tmpErrorMessage: String? = entity.errorMessage
        if (_tmpErrorMessage == null) {
          statement.bindNull(8)
        } else {
          statement.bindText(8, _tmpErrorMessage)
        }
        statement.bindLong(9, entity.attemptCount.toLong())
      }
    }
  }

  public override suspend fun insert(entity: DraftEntity): Long = performSuspending(__db, false, true) { _connection ->
    val _result: Long = __insertAdapterOfDraftEntity.insertAndReturnId(_connection, entity)
    _result
  }

  public override suspend fun getById(id: Long): DraftEntity? {
    val _sql: String = "SELECT * FROM framework_submit_drafts WHERE id = ?"
    return performSuspending(__db, true, false) { _connection ->
      val _stmt: SQLiteStatement = _connection.prepare(_sql)
      try {
        var _argIndex: Int = 1
        _stmt.bindLong(_argIndex, id)
        val _columnIndexOfId: Int = getColumnIndexOrThrow(_stmt, "id")
        val _columnIndexOfFormKey: Int = getColumnIndexOrThrow(_stmt, "formKey")
        val _columnIndexOfUniqueKey: Int = getColumnIndexOrThrow(_stmt, "uniqueKey")
        val _columnIndexOfPayloadJson: Int = getColumnIndexOrThrow(_stmt, "payloadJson")
        val _columnIndexOfStatus: Int = getColumnIndexOrThrow(_stmt, "status")
        val _columnIndexOfCreatedAtMs: Int = getColumnIndexOrThrow(_stmt, "createdAtMs")
        val _columnIndexOfUpdatedAtMs: Int = getColumnIndexOrThrow(_stmt, "updatedAtMs")
        val _columnIndexOfErrorMessage: Int = getColumnIndexOrThrow(_stmt, "errorMessage")
        val _columnIndexOfAttemptCount: Int = getColumnIndexOrThrow(_stmt, "attemptCount")
        val _result: DraftEntity?
        if (_stmt.step()) {
          val _tmpId: Long
          _tmpId = _stmt.getLong(_columnIndexOfId)
          val _tmpFormKey: String
          _tmpFormKey = _stmt.getText(_columnIndexOfFormKey)
          val _tmpUniqueKey: String?
          if (_stmt.isNull(_columnIndexOfUniqueKey)) {
            _tmpUniqueKey = null
          } else {
            _tmpUniqueKey = _stmt.getText(_columnIndexOfUniqueKey)
          }
          val _tmpPayloadJson: String
          _tmpPayloadJson = _stmt.getText(_columnIndexOfPayloadJson)
          val _tmpStatus: String
          _tmpStatus = _stmt.getText(_columnIndexOfStatus)
          val _tmpCreatedAtMs: Long
          _tmpCreatedAtMs = _stmt.getLong(_columnIndexOfCreatedAtMs)
          val _tmpUpdatedAtMs: Long
          _tmpUpdatedAtMs = _stmt.getLong(_columnIndexOfUpdatedAtMs)
          val _tmpErrorMessage: String?
          if (_stmt.isNull(_columnIndexOfErrorMessage)) {
            _tmpErrorMessage = null
          } else {
            _tmpErrorMessage = _stmt.getText(_columnIndexOfErrorMessage)
          }
          val _tmpAttemptCount: Int
          _tmpAttemptCount = _stmt.getLong(_columnIndexOfAttemptCount).toInt()
          _result = DraftEntity(_tmpId,_tmpFormKey,_tmpUniqueKey,_tmpPayloadJson,_tmpStatus,_tmpCreatedAtMs,_tmpUpdatedAtMs,_tmpErrorMessage,_tmpAttemptCount)
        } else {
          _result = null
        }
        _result
      } finally {
        _stmt.close()
      }
    }
  }

  public override suspend fun getPendingByFormKey(formKey: String): DraftEntity? {
    val _sql: String = "SELECT * FROM framework_submit_drafts WHERE formKey = ? AND uniqueKey IS NULL AND status = 'PENDING' LIMIT 1"
    return performSuspending(__db, true, false) { _connection ->
      val _stmt: SQLiteStatement = _connection.prepare(_sql)
      try {
        var _argIndex: Int = 1
        _stmt.bindText(_argIndex, formKey)
        val _columnIndexOfId: Int = getColumnIndexOrThrow(_stmt, "id")
        val _columnIndexOfFormKey: Int = getColumnIndexOrThrow(_stmt, "formKey")
        val _columnIndexOfUniqueKey: Int = getColumnIndexOrThrow(_stmt, "uniqueKey")
        val _columnIndexOfPayloadJson: Int = getColumnIndexOrThrow(_stmt, "payloadJson")
        val _columnIndexOfStatus: Int = getColumnIndexOrThrow(_stmt, "status")
        val _columnIndexOfCreatedAtMs: Int = getColumnIndexOrThrow(_stmt, "createdAtMs")
        val _columnIndexOfUpdatedAtMs: Int = getColumnIndexOrThrow(_stmt, "updatedAtMs")
        val _columnIndexOfErrorMessage: Int = getColumnIndexOrThrow(_stmt, "errorMessage")
        val _columnIndexOfAttemptCount: Int = getColumnIndexOrThrow(_stmt, "attemptCount")
        val _result: DraftEntity?
        if (_stmt.step()) {
          val _tmpId: Long
          _tmpId = _stmt.getLong(_columnIndexOfId)
          val _tmpFormKey: String
          _tmpFormKey = _stmt.getText(_columnIndexOfFormKey)
          val _tmpUniqueKey: String?
          if (_stmt.isNull(_columnIndexOfUniqueKey)) {
            _tmpUniqueKey = null
          } else {
            _tmpUniqueKey = _stmt.getText(_columnIndexOfUniqueKey)
          }
          val _tmpPayloadJson: String
          _tmpPayloadJson = _stmt.getText(_columnIndexOfPayloadJson)
          val _tmpStatus: String
          _tmpStatus = _stmt.getText(_columnIndexOfStatus)
          val _tmpCreatedAtMs: Long
          _tmpCreatedAtMs = _stmt.getLong(_columnIndexOfCreatedAtMs)
          val _tmpUpdatedAtMs: Long
          _tmpUpdatedAtMs = _stmt.getLong(_columnIndexOfUpdatedAtMs)
          val _tmpErrorMessage: String?
          if (_stmt.isNull(_columnIndexOfErrorMessage)) {
            _tmpErrorMessage = null
          } else {
            _tmpErrorMessage = _stmt.getText(_columnIndexOfErrorMessage)
          }
          val _tmpAttemptCount: Int
          _tmpAttemptCount = _stmt.getLong(_columnIndexOfAttemptCount).toInt()
          _result = DraftEntity(_tmpId,_tmpFormKey,_tmpUniqueKey,_tmpPayloadJson,_tmpStatus,_tmpCreatedAtMs,_tmpUpdatedAtMs,_tmpErrorMessage,_tmpAttemptCount)
        } else {
          _result = null
        }
        _result
      } finally {
        _stmt.close()
      }
    }
  }

  public override fun observePendingByFormKey(formKey: String): Flow<DraftEntity?> {
    val _sql: String = "SELECT * FROM framework_submit_drafts WHERE formKey = ? AND uniqueKey IS NULL AND status = 'PENDING' LIMIT 1"
    return createFlow(__db, false, arrayOf("framework_submit_drafts")) { _connection ->
      val _stmt: SQLiteStatement = _connection.prepare(_sql)
      try {
        var _argIndex: Int = 1
        _stmt.bindText(_argIndex, formKey)
        val _columnIndexOfId: Int = getColumnIndexOrThrow(_stmt, "id")
        val _columnIndexOfFormKey: Int = getColumnIndexOrThrow(_stmt, "formKey")
        val _columnIndexOfUniqueKey: Int = getColumnIndexOrThrow(_stmt, "uniqueKey")
        val _columnIndexOfPayloadJson: Int = getColumnIndexOrThrow(_stmt, "payloadJson")
        val _columnIndexOfStatus: Int = getColumnIndexOrThrow(_stmt, "status")
        val _columnIndexOfCreatedAtMs: Int = getColumnIndexOrThrow(_stmt, "createdAtMs")
        val _columnIndexOfUpdatedAtMs: Int = getColumnIndexOrThrow(_stmt, "updatedAtMs")
        val _columnIndexOfErrorMessage: Int = getColumnIndexOrThrow(_stmt, "errorMessage")
        val _columnIndexOfAttemptCount: Int = getColumnIndexOrThrow(_stmt, "attemptCount")
        val _result: DraftEntity?
        if (_stmt.step()) {
          val _tmpId: Long
          _tmpId = _stmt.getLong(_columnIndexOfId)
          val _tmpFormKey: String
          _tmpFormKey = _stmt.getText(_columnIndexOfFormKey)
          val _tmpUniqueKey: String?
          if (_stmt.isNull(_columnIndexOfUniqueKey)) {
            _tmpUniqueKey = null
          } else {
            _tmpUniqueKey = _stmt.getText(_columnIndexOfUniqueKey)
          }
          val _tmpPayloadJson: String
          _tmpPayloadJson = _stmt.getText(_columnIndexOfPayloadJson)
          val _tmpStatus: String
          _tmpStatus = _stmt.getText(_columnIndexOfStatus)
          val _tmpCreatedAtMs: Long
          _tmpCreatedAtMs = _stmt.getLong(_columnIndexOfCreatedAtMs)
          val _tmpUpdatedAtMs: Long
          _tmpUpdatedAtMs = _stmt.getLong(_columnIndexOfUpdatedAtMs)
          val _tmpErrorMessage: String?
          if (_stmt.isNull(_columnIndexOfErrorMessage)) {
            _tmpErrorMessage = null
          } else {
            _tmpErrorMessage = _stmt.getText(_columnIndexOfErrorMessage)
          }
          val _tmpAttemptCount: Int
          _tmpAttemptCount = _stmt.getLong(_columnIndexOfAttemptCount).toInt()
          _result = DraftEntity(_tmpId,_tmpFormKey,_tmpUniqueKey,_tmpPayloadJson,_tmpStatus,_tmpCreatedAtMs,_tmpUpdatedAtMs,_tmpErrorMessage,_tmpAttemptCount)
        } else {
          _result = null
        }
        _result
      } finally {
        _stmt.close()
      }
    }
  }

  public override suspend fun getPendingByUniqueKey(formKey: String, uniqueKey: String): DraftEntity? {
    val _sql: String = "SELECT * FROM framework_submit_drafts WHERE formKey = ? AND uniqueKey = ? AND status = 'PENDING' LIMIT 1"
    return performSuspending(__db, true, false) { _connection ->
      val _stmt: SQLiteStatement = _connection.prepare(_sql)
      try {
        var _argIndex: Int = 1
        _stmt.bindText(_argIndex, formKey)
        _argIndex = 2
        _stmt.bindText(_argIndex, uniqueKey)
        val _columnIndexOfId: Int = getColumnIndexOrThrow(_stmt, "id")
        val _columnIndexOfFormKey: Int = getColumnIndexOrThrow(_stmt, "formKey")
        val _columnIndexOfUniqueKey: Int = getColumnIndexOrThrow(_stmt, "uniqueKey")
        val _columnIndexOfPayloadJson: Int = getColumnIndexOrThrow(_stmt, "payloadJson")
        val _columnIndexOfStatus: Int = getColumnIndexOrThrow(_stmt, "status")
        val _columnIndexOfCreatedAtMs: Int = getColumnIndexOrThrow(_stmt, "createdAtMs")
        val _columnIndexOfUpdatedAtMs: Int = getColumnIndexOrThrow(_stmt, "updatedAtMs")
        val _columnIndexOfErrorMessage: Int = getColumnIndexOrThrow(_stmt, "errorMessage")
        val _columnIndexOfAttemptCount: Int = getColumnIndexOrThrow(_stmt, "attemptCount")
        val _result: DraftEntity?
        if (_stmt.step()) {
          val _tmpId: Long
          _tmpId = _stmt.getLong(_columnIndexOfId)
          val _tmpFormKey: String
          _tmpFormKey = _stmt.getText(_columnIndexOfFormKey)
          val _tmpUniqueKey: String?
          if (_stmt.isNull(_columnIndexOfUniqueKey)) {
            _tmpUniqueKey = null
          } else {
            _tmpUniqueKey = _stmt.getText(_columnIndexOfUniqueKey)
          }
          val _tmpPayloadJson: String
          _tmpPayloadJson = _stmt.getText(_columnIndexOfPayloadJson)
          val _tmpStatus: String
          _tmpStatus = _stmt.getText(_columnIndexOfStatus)
          val _tmpCreatedAtMs: Long
          _tmpCreatedAtMs = _stmt.getLong(_columnIndexOfCreatedAtMs)
          val _tmpUpdatedAtMs: Long
          _tmpUpdatedAtMs = _stmt.getLong(_columnIndexOfUpdatedAtMs)
          val _tmpErrorMessage: String?
          if (_stmt.isNull(_columnIndexOfErrorMessage)) {
            _tmpErrorMessage = null
          } else {
            _tmpErrorMessage = _stmt.getText(_columnIndexOfErrorMessage)
          }
          val _tmpAttemptCount: Int
          _tmpAttemptCount = _stmt.getLong(_columnIndexOfAttemptCount).toInt()
          _result = DraftEntity(_tmpId,_tmpFormKey,_tmpUniqueKey,_tmpPayloadJson,_tmpStatus,_tmpCreatedAtMs,_tmpUpdatedAtMs,_tmpErrorMessage,_tmpAttemptCount)
        } else {
          _result = null
        }
        _result
      } finally {
        _stmt.close()
      }
    }
  }

  public override fun observePendingByUniqueKey(formKey: String, uniqueKey: String): Flow<DraftEntity?> {
    val _sql: String = "SELECT * FROM framework_submit_drafts WHERE formKey = ? AND uniqueKey = ? AND status = 'PENDING' LIMIT 1"
    return createFlow(__db, false, arrayOf("framework_submit_drafts")) { _connection ->
      val _stmt: SQLiteStatement = _connection.prepare(_sql)
      try {
        var _argIndex: Int = 1
        _stmt.bindText(_argIndex, formKey)
        _argIndex = 2
        _stmt.bindText(_argIndex, uniqueKey)
        val _columnIndexOfId: Int = getColumnIndexOrThrow(_stmt, "id")
        val _columnIndexOfFormKey: Int = getColumnIndexOrThrow(_stmt, "formKey")
        val _columnIndexOfUniqueKey: Int = getColumnIndexOrThrow(_stmt, "uniqueKey")
        val _columnIndexOfPayloadJson: Int = getColumnIndexOrThrow(_stmt, "payloadJson")
        val _columnIndexOfStatus: Int = getColumnIndexOrThrow(_stmt, "status")
        val _columnIndexOfCreatedAtMs: Int = getColumnIndexOrThrow(_stmt, "createdAtMs")
        val _columnIndexOfUpdatedAtMs: Int = getColumnIndexOrThrow(_stmt, "updatedAtMs")
        val _columnIndexOfErrorMessage: Int = getColumnIndexOrThrow(_stmt, "errorMessage")
        val _columnIndexOfAttemptCount: Int = getColumnIndexOrThrow(_stmt, "attemptCount")
        val _result: DraftEntity?
        if (_stmt.step()) {
          val _tmpId: Long
          _tmpId = _stmt.getLong(_columnIndexOfId)
          val _tmpFormKey: String
          _tmpFormKey = _stmt.getText(_columnIndexOfFormKey)
          val _tmpUniqueKey: String?
          if (_stmt.isNull(_columnIndexOfUniqueKey)) {
            _tmpUniqueKey = null
          } else {
            _tmpUniqueKey = _stmt.getText(_columnIndexOfUniqueKey)
          }
          val _tmpPayloadJson: String
          _tmpPayloadJson = _stmt.getText(_columnIndexOfPayloadJson)
          val _tmpStatus: String
          _tmpStatus = _stmt.getText(_columnIndexOfStatus)
          val _tmpCreatedAtMs: Long
          _tmpCreatedAtMs = _stmt.getLong(_columnIndexOfCreatedAtMs)
          val _tmpUpdatedAtMs: Long
          _tmpUpdatedAtMs = _stmt.getLong(_columnIndexOfUpdatedAtMs)
          val _tmpErrorMessage: String?
          if (_stmt.isNull(_columnIndexOfErrorMessage)) {
            _tmpErrorMessage = null
          } else {
            _tmpErrorMessage = _stmt.getText(_columnIndexOfErrorMessage)
          }
          val _tmpAttemptCount: Int
          _tmpAttemptCount = _stmt.getLong(_columnIndexOfAttemptCount).toInt()
          _result = DraftEntity(_tmpId,_tmpFormKey,_tmpUniqueKey,_tmpPayloadJson,_tmpStatus,_tmpCreatedAtMs,_tmpUpdatedAtMs,_tmpErrorMessage,_tmpAttemptCount)
        } else {
          _result = null
        }
        _result
      } finally {
        _stmt.close()
      }
    }
  }

  public override fun observeAllByFormKey(formKey: String): Flow<List<DraftEntity>> {
    val _sql: String = "SELECT * FROM framework_submit_drafts WHERE formKey = ? AND status IN ('PENDING','RETRYING','FAILED') ORDER BY createdAtMs DESC"
    return createFlow(__db, false, arrayOf("framework_submit_drafts")) { _connection ->
      val _stmt: SQLiteStatement = _connection.prepare(_sql)
      try {
        var _argIndex: Int = 1
        _stmt.bindText(_argIndex, formKey)
        val _columnIndexOfId: Int = getColumnIndexOrThrow(_stmt, "id")
        val _columnIndexOfFormKey: Int = getColumnIndexOrThrow(_stmt, "formKey")
        val _columnIndexOfUniqueKey: Int = getColumnIndexOrThrow(_stmt, "uniqueKey")
        val _columnIndexOfPayloadJson: Int = getColumnIndexOrThrow(_stmt, "payloadJson")
        val _columnIndexOfStatus: Int = getColumnIndexOrThrow(_stmt, "status")
        val _columnIndexOfCreatedAtMs: Int = getColumnIndexOrThrow(_stmt, "createdAtMs")
        val _columnIndexOfUpdatedAtMs: Int = getColumnIndexOrThrow(_stmt, "updatedAtMs")
        val _columnIndexOfErrorMessage: Int = getColumnIndexOrThrow(_stmt, "errorMessage")
        val _columnIndexOfAttemptCount: Int = getColumnIndexOrThrow(_stmt, "attemptCount")
        val _result: MutableList<DraftEntity> = mutableListOf()
        while (_stmt.step()) {
          val _item: DraftEntity
          val _tmpId: Long
          _tmpId = _stmt.getLong(_columnIndexOfId)
          val _tmpFormKey: String
          _tmpFormKey = _stmt.getText(_columnIndexOfFormKey)
          val _tmpUniqueKey: String?
          if (_stmt.isNull(_columnIndexOfUniqueKey)) {
            _tmpUniqueKey = null
          } else {
            _tmpUniqueKey = _stmt.getText(_columnIndexOfUniqueKey)
          }
          val _tmpPayloadJson: String
          _tmpPayloadJson = _stmt.getText(_columnIndexOfPayloadJson)
          val _tmpStatus: String
          _tmpStatus = _stmt.getText(_columnIndexOfStatus)
          val _tmpCreatedAtMs: Long
          _tmpCreatedAtMs = _stmt.getLong(_columnIndexOfCreatedAtMs)
          val _tmpUpdatedAtMs: Long
          _tmpUpdatedAtMs = _stmt.getLong(_columnIndexOfUpdatedAtMs)
          val _tmpErrorMessage: String?
          if (_stmt.isNull(_columnIndexOfErrorMessage)) {
            _tmpErrorMessage = null
          } else {
            _tmpErrorMessage = _stmt.getText(_columnIndexOfErrorMessage)
          }
          val _tmpAttemptCount: Int
          _tmpAttemptCount = _stmt.getLong(_columnIndexOfAttemptCount).toInt()
          _item = DraftEntity(_tmpId,_tmpFormKey,_tmpUniqueKey,_tmpPayloadJson,_tmpStatus,_tmpCreatedAtMs,_tmpUpdatedAtMs,_tmpErrorMessage,_tmpAttemptCount)
          _result.add(_item)
        }
        _result
      } finally {
        _stmt.close()
      }
    }
  }

  public override suspend fun getAllPending(): List<DraftEntity> {
    val _sql: String = "SELECT * FROM framework_submit_drafts WHERE status = 'PENDING'"
    return performSuspending(__db, true, false) { _connection ->
      val _stmt: SQLiteStatement = _connection.prepare(_sql)
      try {
        val _columnIndexOfId: Int = getColumnIndexOrThrow(_stmt, "id")
        val _columnIndexOfFormKey: Int = getColumnIndexOrThrow(_stmt, "formKey")
        val _columnIndexOfUniqueKey: Int = getColumnIndexOrThrow(_stmt, "uniqueKey")
        val _columnIndexOfPayloadJson: Int = getColumnIndexOrThrow(_stmt, "payloadJson")
        val _columnIndexOfStatus: Int = getColumnIndexOrThrow(_stmt, "status")
        val _columnIndexOfCreatedAtMs: Int = getColumnIndexOrThrow(_stmt, "createdAtMs")
        val _columnIndexOfUpdatedAtMs: Int = getColumnIndexOrThrow(_stmt, "updatedAtMs")
        val _columnIndexOfErrorMessage: Int = getColumnIndexOrThrow(_stmt, "errorMessage")
        val _columnIndexOfAttemptCount: Int = getColumnIndexOrThrow(_stmt, "attemptCount")
        val _result: MutableList<DraftEntity> = mutableListOf()
        while (_stmt.step()) {
          val _item: DraftEntity
          val _tmpId: Long
          _tmpId = _stmt.getLong(_columnIndexOfId)
          val _tmpFormKey: String
          _tmpFormKey = _stmt.getText(_columnIndexOfFormKey)
          val _tmpUniqueKey: String?
          if (_stmt.isNull(_columnIndexOfUniqueKey)) {
            _tmpUniqueKey = null
          } else {
            _tmpUniqueKey = _stmt.getText(_columnIndexOfUniqueKey)
          }
          val _tmpPayloadJson: String
          _tmpPayloadJson = _stmt.getText(_columnIndexOfPayloadJson)
          val _tmpStatus: String
          _tmpStatus = _stmt.getText(_columnIndexOfStatus)
          val _tmpCreatedAtMs: Long
          _tmpCreatedAtMs = _stmt.getLong(_columnIndexOfCreatedAtMs)
          val _tmpUpdatedAtMs: Long
          _tmpUpdatedAtMs = _stmt.getLong(_columnIndexOfUpdatedAtMs)
          val _tmpErrorMessage: String?
          if (_stmt.isNull(_columnIndexOfErrorMessage)) {
            _tmpErrorMessage = null
          } else {
            _tmpErrorMessage = _stmt.getText(_columnIndexOfErrorMessage)
          }
          val _tmpAttemptCount: Int
          _tmpAttemptCount = _stmt.getLong(_columnIndexOfAttemptCount).toInt()
          _item = DraftEntity(_tmpId,_tmpFormKey,_tmpUniqueKey,_tmpPayloadJson,_tmpStatus,_tmpCreatedAtMs,_tmpUpdatedAtMs,_tmpErrorMessage,_tmpAttemptCount)
          _result.add(_item)
        }
        _result
      } finally {
        _stmt.close()
      }
    }
  }

  public override fun observeAll(): Flow<List<DraftEntity>> {
    val _sql: String = "SELECT * FROM framework_submit_drafts WHERE status IN ('PENDING','RETRYING','FAILED') ORDER BY updatedAtMs DESC"
    return createFlow(__db, false, arrayOf("framework_submit_drafts")) { _connection ->
      val _stmt: SQLiteStatement = _connection.prepare(_sql)
      try {
        val _columnIndexOfId: Int = getColumnIndexOrThrow(_stmt, "id")
        val _columnIndexOfFormKey: Int = getColumnIndexOrThrow(_stmt, "formKey")
        val _columnIndexOfUniqueKey: Int = getColumnIndexOrThrow(_stmt, "uniqueKey")
        val _columnIndexOfPayloadJson: Int = getColumnIndexOrThrow(_stmt, "payloadJson")
        val _columnIndexOfStatus: Int = getColumnIndexOrThrow(_stmt, "status")
        val _columnIndexOfCreatedAtMs: Int = getColumnIndexOrThrow(_stmt, "createdAtMs")
        val _columnIndexOfUpdatedAtMs: Int = getColumnIndexOrThrow(_stmt, "updatedAtMs")
        val _columnIndexOfErrorMessage: Int = getColumnIndexOrThrow(_stmt, "errorMessage")
        val _columnIndexOfAttemptCount: Int = getColumnIndexOrThrow(_stmt, "attemptCount")
        val _result: MutableList<DraftEntity> = mutableListOf()
        while (_stmt.step()) {
          val _item: DraftEntity
          val _tmpId: Long
          _tmpId = _stmt.getLong(_columnIndexOfId)
          val _tmpFormKey: String
          _tmpFormKey = _stmt.getText(_columnIndexOfFormKey)
          val _tmpUniqueKey: String?
          if (_stmt.isNull(_columnIndexOfUniqueKey)) {
            _tmpUniqueKey = null
          } else {
            _tmpUniqueKey = _stmt.getText(_columnIndexOfUniqueKey)
          }
          val _tmpPayloadJson: String
          _tmpPayloadJson = _stmt.getText(_columnIndexOfPayloadJson)
          val _tmpStatus: String
          _tmpStatus = _stmt.getText(_columnIndexOfStatus)
          val _tmpCreatedAtMs: Long
          _tmpCreatedAtMs = _stmt.getLong(_columnIndexOfCreatedAtMs)
          val _tmpUpdatedAtMs: Long
          _tmpUpdatedAtMs = _stmt.getLong(_columnIndexOfUpdatedAtMs)
          val _tmpErrorMessage: String?
          if (_stmt.isNull(_columnIndexOfErrorMessage)) {
            _tmpErrorMessage = null
          } else {
            _tmpErrorMessage = _stmt.getText(_columnIndexOfErrorMessage)
          }
          val _tmpAttemptCount: Int
          _tmpAttemptCount = _stmt.getLong(_columnIndexOfAttemptCount).toInt()
          _item = DraftEntity(_tmpId,_tmpFormKey,_tmpUniqueKey,_tmpPayloadJson,_tmpStatus,_tmpCreatedAtMs,_tmpUpdatedAtMs,_tmpErrorMessage,_tmpAttemptCount)
          _result.add(_item)
        }
        _result
      } finally {
        _stmt.close()
      }
    }
  }

  public override suspend fun markRetrying(id: Long, nowMs: Long) {
    val _sql: String = "UPDATE framework_submit_drafts SET status = 'RETRYING', updatedAtMs = ?, attemptCount = attemptCount + 1 WHERE id = ?"
    return performSuspending(__db, false, true) { _connection ->
      val _stmt: SQLiteStatement = _connection.prepare(_sql)
      try {
        var _argIndex: Int = 1
        _stmt.bindLong(_argIndex, nowMs)
        _argIndex = 2
        _stmt.bindLong(_argIndex, id)
        _stmt.step()
      } finally {
        _stmt.close()
      }
    }
  }

  public override suspend fun markSubmitted(id: Long, nowMs: Long) {
    val _sql: String = "UPDATE framework_submit_drafts SET status = 'SUBMITTED', updatedAtMs = ? WHERE id = ?"
    return performSuspending(__db, false, true) { _connection ->
      val _stmt: SQLiteStatement = _connection.prepare(_sql)
      try {
        var _argIndex: Int = 1
        _stmt.bindLong(_argIndex, nowMs)
        _argIndex = 2
        _stmt.bindLong(_argIndex, id)
        _stmt.step()
      } finally {
        _stmt.close()
      }
    }
  }

  public override suspend fun requeue(id: Long, nowMs: Long) {
    val _sql: String = "UPDATE framework_submit_drafts SET status = 'PENDING', errorMessage = NULL, updatedAtMs = ? WHERE id = ?"
    return performSuspending(__db, false, true) { _connection ->
      val _stmt: SQLiteStatement = _connection.prepare(_sql)
      try {
        var _argIndex: Int = 1
        _stmt.bindLong(_argIndex, nowMs)
        _argIndex = 2
        _stmt.bindLong(_argIndex, id)
        _stmt.step()
      } finally {
        _stmt.close()
      }
    }
  }

  public override suspend fun markFailed(
    id: Long,
    nowMs: Long,
    error: String?,
  ) {
    val _sql: String = "UPDATE framework_submit_drafts SET status = 'FAILED', updatedAtMs = ?, errorMessage = ? WHERE id = ?"
    return performSuspending(__db, false, true) { _connection ->
      val _stmt: SQLiteStatement = _connection.prepare(_sql)
      try {
        var _argIndex: Int = 1
        _stmt.bindLong(_argIndex, nowMs)
        _argIndex = 2
        if (error == null) {
          _stmt.bindNull(_argIndex)
        } else {
          _stmt.bindText(_argIndex, error)
        }
        _argIndex = 3
        _stmt.bindLong(_argIndex, id)
        _stmt.step()
      } finally {
        _stmt.close()
      }
    }
  }

  public override suspend fun updatePayload(
    id: Long,
    payloadJson: String,
    nowMs: Long,
  ) {
    val _sql: String = "UPDATE framework_submit_drafts SET payloadJson = ?, updatedAtMs = ? WHERE id = ?"
    return performSuspending(__db, false, true) { _connection ->
      val _stmt: SQLiteStatement = _connection.prepare(_sql)
      try {
        var _argIndex: Int = 1
        _stmt.bindText(_argIndex, payloadJson)
        _argIndex = 2
        _stmt.bindLong(_argIndex, nowMs)
        _argIndex = 3
        _stmt.bindLong(_argIndex, id)
        _stmt.step()
      } finally {
        _stmt.close()
      }
    }
  }

  public override suspend fun deleteByFormKey(formKey: String) {
    val _sql: String = "DELETE FROM framework_submit_drafts WHERE formKey = ?"
    return performSuspending(__db, false, true) { _connection ->
      val _stmt: SQLiteStatement = _connection.prepare(_sql)
      try {
        var _argIndex: Int = 1
        _stmt.bindText(_argIndex, formKey)
        _stmt.step()
      } finally {
        _stmt.close()
      }
    }
  }

  public override suspend fun deleteByUniqueKey(formKey: String, uniqueKey: String) {
    val _sql: String = "DELETE FROM framework_submit_drafts WHERE formKey = ? AND uniqueKey = ?"
    return performSuspending(__db, false, true) { _connection ->
      val _stmt: SQLiteStatement = _connection.prepare(_sql)
      try {
        var _argIndex: Int = 1
        _stmt.bindText(_argIndex, formKey)
        _argIndex = 2
        _stmt.bindText(_argIndex, uniqueKey)
        _stmt.step()
      } finally {
        _stmt.close()
      }
    }
  }

  public override suspend fun deleteById(id: Long) {
    val _sql: String = "DELETE FROM framework_submit_drafts WHERE id = ?"
    return performSuspending(__db, false, true) { _connection ->
      val _stmt: SQLiteStatement = _connection.prepare(_sql)
      try {
        var _argIndex: Int = 1
        _stmt.bindLong(_argIndex, id)
        _stmt.step()
      } finally {
        _stmt.close()
      }
    }
  }

  public override suspend fun deleteAll() {
    val _sql: String = "DELETE FROM framework_submit_drafts"
    return performSuspending(__db, false, true) { _connection ->
      val _stmt: SQLiteStatement = _connection.prepare(_sql)
      try {
        _stmt.step()
      } finally {
        _stmt.close()
      }
    }
  }

  public override suspend fun deleteOlderThan(thresholdMs: Long) {
    val _sql: String = "DELETE FROM framework_submit_drafts WHERE createdAtMs < ? AND status IN ('SUBMITTED', 'FAILED')"
    return performSuspending(__db, false, true) { _connection ->
      val _stmt: SQLiteStatement = _connection.prepare(_sql)
      try {
        var _argIndex: Int = 1
        _stmt.bindLong(_argIndex, thresholdMs)
        _stmt.step()
      } finally {
        _stmt.close()
      }
    }
  }

  public companion object {
    public fun getRequiredColumnConverters(): List<KClass<*>> = emptyList()

    public fun getRequiredDaoReturnTypeConverters(): List<KClass<*>> = emptyList()
  }
}
